/* PACKAGES */
import React, { useCallback, useEffect, useState, useRef } from 'react';

/* ELEMENTS */
import SliderArrows from './SliderArrows';
import SliderCounter from './SliderCounter';
import SliderNav from './SliderNav';
import SliderOther from './SliderOther';
import SliderThumbs from './SliderThumbs';

/* UTILS */
import { addEvent, removeEvent, addClass, removeClass } from '../../utils/dom';
import { getDragX, getIndex, getDynamicSlideWidths } from '../../utils/slider';

/* STYLES */
import s from './Slider.module.scss';

const Slider = ({
	startIndex = 0,
	infinite = 1,
	hint = false,
	widthSubColumn = false,
	totalSubColumns = false,
	dynamicSlideWidth = false,
	slideGap = 0,
	autoplay = false,
	autoplayFilm = true,
	showThumbs = false,
	showArrows = true,
	showNav = true,
	showCounter = false,
	showOther = false,
	adaption = false,
	alignment = 'left',
	transitionType = 'slide',
	interactionTypes = ['touch'],
	screenMaxWidthDisable = false,
	screenMinWidthDisable = false,
	breakPoint = 'desktop',
	children,
	...props
}) => {
	const [partialIndex, setPartialIndex] = useState(0);
	const [slideWidth, setSlideWidth] = useState(100);
	const [index, setIndex] = useState(startIndex + ((widthSubColumn || 1) * infinite));
	const [transition, setTransition] = useState(false);
	const [disabled, setDisabled] = useState(children.length === 1);

	const slider = useRef(null);
	const sliderInner = useRef(null);
	const autoplayPointer = useRef(null);
	const dragStart = useRef(0);
	const lastIndex = useRef(index);
	const counter = useRef(index);

	const goToSlide = useCallback((nextIndex, e, makeTransition = true) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();

			if (autoplay) window.clearInterval(autoplayPointer.current);
		}

		const previousIndex = counter.current;

		if (transitionType === 'fade') {
			nextIndex = getIndex(infinite, children.length, false, widthSubColumn, totalSubColumns, nextIndex);
			if (sliderInner.current?.children?.[previousIndex]) {
				setTimeout(() => {
					removeClass(sliderInner.current.children[previousIndex], 'u_selected');
				}, 200);
			}
			if (sliderInner.current?.children?.[nextIndex]) addClass(sliderInner.current.children[nextIndex], 'u_selected');
		}

		// Pause film if previous child had video element.
		const $childCurrentElement = sliderInner.current?.children?.[index];
		const pauseVideoElements = $childCurrentElement ? $childCurrentElement.querySelectorAll('video') : false;
		if (pauseVideoElements && pauseVideoElements.length > 0) {
			[...pauseVideoElements].forEach((videoElement) => {
				videoElement.pause();
			});
		}

		// Start film if child has video element.
		const $childNextElement = sliderInner.current?.children?.[nextIndex];
		const playVideoElements = $childNextElement ? $childNextElement.querySelectorAll('video') : false;
		if (playVideoElements && playVideoElements.length > 0 && autoplayFilm) {
			[...playVideoElements].forEach((videoElement) => {
				videoElement.play();
			});
		}

		counter.current = nextIndex;
		setIndex(nextIndex);
		lastIndex.current = previousIndex;
		setTransition(makeTransition);
	}, [autoplay, children, index, transitionType, autoplayFilm, widthSubColumn, totalSubColumns, infinite]);

	const autoplayFunction = useCallback(() => {
		goToSlide(counter.current + 1);
	}, [goToSlide]);

	useEffect(() => {
		if (!totalSubColumns) return;
		if (disabled && totalSubColumns > 2 && breakPoint === 'mobile') {
			setDisabled(false);
		} else if (disabled && totalSubColumns > 3 && breakPoint === 'tablet') {
			setDisabled(false);
		} else if (!disabled && totalSubColumns === 3 && breakPoint === 'tablet') {
			setDisabled(true);
		} else if (!disabled && totalSubColumns < 3 && breakPoint !== 'mobile') {
			setDisabled(true);
		}
	}, [disabled, totalSubColumns, breakPoint]);

	useEffect(() => {
		const $sliderInner = sliderInner?.current?.parentNode;

		if (autoplay && children.length > 1) {
			autoplayPointer.current = window.setInterval(autoplayFunction, autoplay);

			addEvent($sliderInner, 'click.slider', () => {
				window.clearInterval(autoplayPointer.current);
			}, { passive: false });
		}

		return () => {
			window.clearInterval(autoplayPointer.current);
			if ($sliderInner) removeEvent($sliderInner, 'click.slider', { passive: false });
		};
	}, []);

	useEffect(() => {
		const elementsInitiated = () => {
			if (!sliderInner.current) {
				setTimeout(() => {
					elementsInitiated();
				}, 50);
				return;
			}
			const $slides = sliderInner.current?.children || [];

			const currentElement = $slides?.[index];
			if (currentElement) {
				if (transitionType === 'fade') addClass($slides[index], 'u_selected');

				const videoElements = currentElement.querySelectorAll('video');
				if (videoElements.length > 0 && autoplayFilm) {
					[...videoElements].forEach((videoElement) => {
						videoElement.play();
					});
				}
			}
		};

		elementsInitiated();
	}, [children, index, transitionType, autoplayFilm, sliderInner]);

	useEffect(() => {
		const resizeEvent = () => {
			let firstRun = true;

			if ((screenMaxWidthDisable !== false || screenMinWidthDisable !== false) && children.length > 1) {
				const screenWidth = window.innerWidth;
				let isDisabled = true;

				// Only one child or less || Screen is over max screenWidth || Screen is under min screenWidth -> disable
				const disable = children.length <= 1
					|| (screenMaxWidthDisable !== false && screenWidth > screenMaxWidthDisable)
					|| (screenMinWidthDisable !== false && screenWidth < screenMinWidthDisable);

				// More than one child && Screen is over max screenWidth if set || Screen is under min screenWidth if set -> enable
				const enable = children.length > 1
					&& ((screenMaxWidthDisable !== false && screenWidth <= screenMaxWidthDisable) || screenMaxWidthDisable === false)
					&& ((screenMinWidthDisable !== false && screenWidth >= screenMinWidthDisable) || screenMinWidthDisable === false);

				if (disable && (!isDisabled || firstRun)) {
					isDisabled = true;

					if (autoplay) window.clearInterval(autoplayPointer.current);
					setDisabled(true);

					removeEvent(document.body, 'touchmove.slider', { passive: false });
				} else if (enable && (isDisabled || firstRun)) {
					isDisabled = false;

					if (autoplay) autoplayPointer.current = window.setInterval(autoplayFunction, autoplay);
					setDisabled(false);

					removeEvent(document.body, 'touchmove.slider', { passive: false });
					addEvent(document.body, 'touchmove.slider', (e) => {
						if (sliderInner.current && sliderInner.current.contains(e.target)) {
							if (Math.abs(e.touches[0].pageX - dragStart.current) > 20) {
								e.preventDefault();
							}
						}
					}, { passive: false });
				}
			} else {
				// Never disabled prevent scrolling always
				removeEvent(document.body, 'touchmove.slider', { passive: false });
				addEvent(document.body, 'touchmove.slider', (e) => {
					if (sliderInner.current && sliderInner.current.contains(e.target)) {
						if (Math.abs(e.touches[0].pageX - dragStart.current) > 20) {
							e.preventDefault();
						}
					}
				}, { passive: false });
			}

			if (dynamicSlideWidth && !disabled && sliderInner.current && transitionType !== 'fade') {
				const { width, transform } = getDynamicSlideWidths(slider.current, sliderInner.current, infinite, slideGap, index);
				sliderInner.current.style.width = width;
				sliderInner.current.style.transform = transform;
			}

			firstRun = false;
		};

		resizeEvent();

		addEvent(window, 'resize.slider', () => {
			window.requestAnimationFrame(resizeEvent);
		}, { passive: false });

		return () => {
			removeEvent(document.body, 'touchmove.slider', { passive: false });
			removeEvent(window, 'resize.slider', { passive: false });
		};
	}, [screenMaxWidthDisable, screenMinWidthDisable, children, autoplay, autoplayFunction, dynamicSlideWidth, disabled, infinite, slideGap, index]);

	useEffect(() => {
		if (sliderInner.current && dynamicSlideWidth && !disabled && sliderInner.current && transitionType !== 'fade') {
			const { width, transform } = getDynamicSlideWidths(slider.current, sliderInner.current, infinite, slideGap, index);
			sliderInner.current.style.width = width;
			sliderInner.current.style.transform = transform;
		}
	}, [children, slider, sliderInner, infinite, slideGap, index, dynamicSlideWidth, disabled, transitionType]);

	// If disabled then remove.
	if (disabled) return children;

	const handleDragStart = (e) => {
		const x = getDragX(e);

		if (autoplay) window.clearInterval(autoplayPointer.current);

		dragStart.current = x;
		setTransition(false);
		setPartialIndex(0);
		setSlideWidth(slider.current.offsetWidth);
	};

	const handleDragMove = (e) => {
		const x = getDragX(e);
		const offset = dragStart.current - x;
		const tempPartialIndex = offset / slideWidth;

		setPartialIndex(tempPartialIndex);
	};

	const handleDragEnd = () => {
		// If over 10% then apply transition.
		if (Math.abs(partialIndex * 100) > 20) {
			const nextIndex = partialIndex > 0 ? index + 1 : index - 1;

			// Pause film if previous child had video element.
			const $childCurrentElement = sliderInner.current?.children?.[index];
			const pauseVideoElements = $childCurrentElement ? $childCurrentElement.querySelectorAll('video') : false;
			if (pauseVideoElements && pauseVideoElements.length > 0) {
				[...pauseVideoElements].forEach((videoElement) => {
					videoElement.pause();
				});
			}

			// Start film if child has video element.
			const $childNextElement = sliderInner.current?.children?.[nextIndex];
			const playVideoElements = $childNextElement ? $childNextElement.querySelectorAll('video') : false;
			if (playVideoElements && playVideoElements.length > 0 && autoplayFilm) {
				[...playVideoElements].forEach((videoElement) => {
					videoElement.play();
				});
			}

			if (transitionType === 'fade') {
				if (sliderInner.current?.children?.[index]) removeClass(sliderInner.current.children[index], 'u_selected');
				if (sliderInner.current?.children?.[nextIndex]) addClass(sliderInner.current.children[nextIndex], 'u_selected');
			}

			dragStart.current = 0;
			setPartialIndex(0);
			setIndex(nextIndex);
			lastIndex.current = index;
			setTransition(true);
		} else {
			dragStart.current = 0;
			setPartialIndex(0);
			setTransition(true);
		}
	};

	const handleTransitionEnd = () => {
		const newIndex = getIndex(infinite, children.length, false, widthSubColumn, totalSubColumns, index);

		if (newIndex !== index) {
			setIndex(newIndex);
			counter.current = newIndex;
			setTransition(false);
		}
	};

	const handleClick = (e, dir) => {
		e.preventDefault();
		goToSlide(lastIndex.current + dir, e);
	};

	const numberSlides = children.length + (infinite * 2);
	const totalUniqueSlides = totalSubColumns || children.length;

	const slidesStyles = {};
	if (transitionType !== 'fade') {
		const translatePx = Math.round(slideWidth * partialIndex) * -1;
		if (hint) {
			slidesStyles.width = `${100 * numberSlides}vw`;
			slidesStyles.transform = `translateX(calc(${(-1 * index * 100) + (14 * index) + 7}vw + ${translatePx}px + var(--translate-offset)))`;
		} else if (widthSubColumn && totalSubColumns) {
			// Uneven amount of columns remove from total due to duplication of less than full column.
			// ((widthSubColumn - (totalSubColumns % widthSubColumn)) * (infinite ? 1 : 0))
			// Technically should duplicate subcolumns instead. Duplicated columns should be last x and first x subcolumns.
			const numberTotalSubColumns = totalSubColumns + (widthSubColumn * (infinite * 2)); // - ((widthSubColumn - (totalSubColumns % widthSubColumn)) * (infinite ? 1 : 0));
			const totalSlideContainers = Math.ceil(numberTotalSubColumns / widthSubColumn);
			let showHalfSlidePercent = alignment !== 'center' ? ((widthSubColumn * 2) / ((widthSubColumn * 2) + 1)) : 1;

			if (breakPoint === 'desktop') {
				// Get the amount of full columns + remaining columns
				// widthSubColumn - 1/2 subColumn - 6/7 || 8/9
				slidesStyles.width = `${100 * totalSlideContainers * showHalfSlidePercent}vw`; // One slide = 100%
			}
			if (breakPoint === 'tablet') {
				// widthSubColumn + 1/2 subColumn - 6/5 || 8/7
				showHalfSlidePercent = alignment !== 'center' ? ((widthSubColumn * 2) / ((widthSubColumn * 2) - 1)) : 1;
				slidesStyles.width = `${100 * totalSlideContainers * showHalfSlidePercent}vw`; // One slide - one column = 100%
			}
			if (breakPoint === 'mobile') {
				// widthSubColumn + 1 1/2 subColumn - 6/3 || 8/5
				showHalfSlidePercent = alignment !== 'center' ? ((widthSubColumn * 2) / ((widthSubColumn * 2) - 3)) : 1;
				slidesStyles.width = `${100 * totalSlideContainers * showHalfSlidePercent}vw`; // One slide - two columns = 100%
			}

			// 100%
			slidesStyles.transform = `translateX(calc(${-1 * index * (100 / numberTotalSubColumns)}% + ${translatePx}px + var(--translate-offset)))`;
		} else if (dynamicSlideWidth) {
			slidesStyles.width = 'auto';
			slidesStyles.transform = 'translateX(0)';
		} else {
			slidesStyles.width = `${100 * numberSlides}%`;
			slidesStyles.transform = `translateX(calc(${-1 * index * (100 / numberSlides)}% + ${translatePx}px + var(--translate-offset)))`;
		}
	}

	const slidesClasses = [s.slider_slides, 'u_slideMain'];
	if (transition) slidesClasses.push(s.slider_slides__transition);

	const sliderClasses = [s.slider];
	if (adaption !== false) sliderClasses.push(s[`slider__${adaption}`]);
	if (transitionType === 'fade') sliderClasses.push(s.slider__fade);
	if (hint) sliderClasses.push(s.slider__hint);

	let slideElements = React.Children.toArray(children);
	if (infinite) {
		const startElements = React.Children.toArray(children).slice(-1 * infinite);
		const lastElements = React.Children.toArray(children).slice(0, infinite);

		slideElements = [
			// eslint-disable-next-line react/no-array-index-key
			...(React.Children.map(startElements, (startElement, i) => React.cloneElement(startElement, { key: `startElement_${i}` }))),
			...slideElements,
			// eslint-disable-next-line react/no-array-index-key
			...(React.Children.map(lastElements, (lastElement, i) => React.cloneElement(lastElement, { key: `lastElement_${i}` }))),
		];
	}

	const interactionEvents = {
		onTransitionEnd: handleTransitionEnd,
	};

	if (interactionTypes.indexOf('touch') !== -1) {
		interactionEvents.onTouchStart = handleDragStart;
		interactionEvents.onTouchMove = handleDragMove;
		interactionEvents.onTouchEnd = handleDragEnd;
	}
	if (interactionTypes.indexOf('click') !== -1) {
		interactionEvents.onClick = (e) => handleClick(e, 1);
	}
	if (interactionTypes.indexOf('rightClick') !== -1) {
		interactionEvents.onContextMenu = (e) => handleClick(e, -1);
	}

	return (
		<div className={sliderClasses.join(' ')} ref={slider}>
			{ showArrows && <SliderArrows infinite={infinite} totalUniqueSlides={totalUniqueSlides - widthSubColumn} index={index} goToSlide={goToSlide} /> }
			{ showNav && <SliderNav infinite={infinite} totalUniqueSlides={totalUniqueSlides} widthSubColumn={widthSubColumn} totalSubColumns={totalSubColumns} index={index} goToSlide={goToSlide} /> }
			{ showCounter && <SliderCounter infinite={infinite} totalUniqueSlides={totalUniqueSlides} widthSubColumn={widthSubColumn} totalSubColumns={totalSubColumns} index={index} /> }
			<div className={s.slider_inner} {...interactionEvents}>
				<div className={slidesClasses.join(' ')} ref={sliderInner} style={slidesStyles}>
					{ slideElements }
				</div>
			</div>
			{ showThumbs && <SliderThumbs infinite={infinite} childrenCopy={children} index={index} goToSlide={goToSlide} /> }
			{ showOther && <SliderOther childrenCopy={children} Other={showOther} {...props} index={index} /> }
		</div>
	);
};

export default Slider;
